/**
 * useConversations Hook
 *
 * Manages conversation state including fetching, creating, and sending messages.
 * Follows the same pattern as useProposals for consistency.
 */

import {
  createSignal,
  createResource,
  createMemo,
  createEffect,
  on,
  Accessor,
} from "solid-js";
import type {
  Conversation,
  ConversationId,
  ConversationWithMessages,
  ChatViewContext,
  ProjectId,
  Message,
  MessageId,
  Proposal,
  ProposalId,
  BoardEvent,
} from "../api/types";
import {
  listConversations,
  createConversation,
  getConversation,
  sendMessageStreaming,
  updateConversationTitle,
  setConversationStarred,
  deleteMessage as apiDeleteMessage,
  ChatResponse,
} from "../api/conversations";
import { api } from "../api/client";
import { surfaceQuotaExhausted } from "../utils/aiError";

/** Result type for useConversations hook */
export interface UseConversationsResult {
  /** All conversations for the project */
  conversations: Accessor<Conversation[] | undefined>;
  /** Loading state for conversations list */
  loading: Accessor<boolean>;
  /** Error from conversations fetch */
  error: Accessor<Error | undefined>;
  /** Currently selected conversation (with messages) */
  selectedConversation: Accessor<ConversationWithMessages | undefined>;
  /** Loading state for selected conversation */
  selectedLoading: Accessor<boolean>;
  /** Error from the selected conversation fetch */
  selectedError: Accessor<Error | undefined>;
  /** Messages for the current conversation */
  messages: Accessor<Message[]>;
  /** Whether AI is currently responding */
  isResponding: Accessor<boolean>;
  /** Streaming text content being received from AI (empty when not streaming) */
  streamingContent: Accessor<string>;
  /** Select a conversation to view */
  select: (id: ConversationId | null) => void;
  /** Create a new conversation */
  create: (title?: string) => Promise<Conversation>;
  /** Send a message to current conversation */
  send: (
    content: string,
    viewContext?: ChatViewContext,
  ) => Promise<ChatResponse>;
  /** Cancel the in-flight AI request */
  cancel: () => void;
  /** Refetch messages for the current conversation */
  refetchMessages: () => Promise<void>;
  /** Refetch the conversations list */
  refetch: () => void;
  /** Update a conversation's title */
  updateTitle: (id: ConversationId, title: string) => Promise<void>;
  /** Toggle the starred state of a conversation (optimistic) */
  toggleStar: (conversationId: ConversationId) => Promise<void>;
  /** Look up a proposal hydrated with the current conversation (for inline cards) */
  getProposal: (id: ProposalId) => Proposal | undefined;
  /** Upsert a proposal into the conversation's proposal store (after approve/reject) */
  applyProposalUpdate: (proposal: Proposal) => void;
  /** Refetch a single proposal by ID and upsert it (e.g. after a WebSocket update) */
  refreshProposal: (id: ProposalId) => Promise<void>;
  /** Handle WebSocket events for conversations */
  handleWebSocketEvent: (event: BoardEvent) => void;
  /**
   * Delete a message and all subsequent messages, then re-send with new
   * content (the edit and regenerate flows) or just prune (delete flow). If
   * the user switched conversations while the delete was in flight the
   * resend is dropped, and its content is preserved in the originating
   * conversation as a failed send (FLOW-818).
   */
  deleteMessageAndResend: (
    messageId: MessageId,
    resendContent?: string,
    viewContext?: ChatViewContext,
  ) => Promise<void>;
  /** Retry sending a failed message */
  retryMessage: (
    messageId: MessageId,
    viewContext?: ChatViewContext,
  ) => Promise<ChatResponse>;
}

/** Optional host wiring for useConversations */
export interface UseConversationsOptions {
  /**
   * Called with user-facing copy when a resend had to be dropped because
   * the user switched conversations mid-delete (FLOW-818). Wire it to
   * a toast: the inline report lives in the conversation the user just left,
   * so unlike FLOW-692's failed sends there is nothing on screen saying it.
   */
  onDroppedEdit?: (message: string) => void;
}

/**
 * Hook for managing conversations in the Chat UI.
 *
 * @param projectId - Accessor for the current project ID
 * @param options - Optional host wiring (see UseConversationsOptions)
 * @returns UseConversationsResult with conversation state and actions
 */
export function useConversations(
  projectId: Accessor<ProjectId>,
  options: UseConversationsOptions = {},
): UseConversationsResult {
  const [selectedId, setSelectedId] = createSignal<ConversationId | null>(null);

  // Conversation currently being responded to (null when idle). Used to scope
  // `isResponding` and `streamingContent` to the originating conversation so a
  // mid-stream switch doesn't leak A's response into B's view (FLOW-301).
  const [respondingConvId, setRespondingConvId] =
    createSignal<ConversationId | null>(null);

  const [isRespondingInternal, setIsRespondingInternal] = createSignal(false);

  const [streamingContentInternal, setStreamingContentInternal] =
    createSignal("");

  // Public accessors: only surface streaming state when the user is viewing the
  // conversation that's currently being responded to.
  const isResponding = createMemo(
    () => isRespondingInternal() && respondingConvId() === selectedId(),
  );
  const streamingContent = createMemo(() =>
    respondingConvId() === selectedId() ? streamingContentInternal() : "",
  );

  let activeController: AbortController | null = null;

  const [localMessages, setLocalMessages] = createSignal<Message[]>([]);

  // Ids of optimistic user messages whose send() is still in flight. Selecting
  // a just-created conversation fires its resource fetch in the background,
  // and that fetch can resolve mid-send — syncMessages must not let the
  // server's (necessarily stale) list wipe a message the user is still
  // waiting on (FLOW-812).
  const pendingSendIds = new Set<MessageId>();

  // Sends that have finished, per conversation. A fetch handled before a send
  // finished cannot contain that send's messages — the user bubble is no
  // longer pending and the assistant message's id was minted by the stream —
  // so replacing the thread with such a list wipes a completed exchange off
  // the screen (FLOW-882). Compare the count a fetch started with against the
  // count when it resolves to tell those fetches apart.
  const completedSends = new Map<ConversationId, number>();
  const sendCount = (convId: ConversationId): number =>
    completedSends.get(convId) ?? 0;

  // Proposals referenced by the current conversation's messages, hydrated by the
  // backend with each conversation load. Keyed by id so inline proposal cards read
  // from here instead of each fetching `GET /proposals/{id}` on mount.
  const [conversationProposals, setConversationProposals] = createSignal<
    Record<ProposalId, Proposal>
  >({});

  const indexProposals = (
    proposals: Proposal[] | undefined,
  ): Record<ProposalId, Proposal> => {
    const byId: Record<ProposalId, Proposal> = {};
    for (const p of proposals ?? []) byId[p.id] = p;
    return byId;
  };

  const getProposal = (id: ProposalId): Proposal | undefined =>
    conversationProposals()[id];

  const applyProposalUpdate = (proposal: Proposal) =>
    setConversationProposals((prev) => ({ ...prev, [proposal.id]: proposal }));

  // Fetch a single proposal and upsert it. One request per actual change — never
  // a fan-out across the conversation's proposals.
  const refreshProposal = async (id: ProposalId): Promise<void> => {
    const proposal = await api.get<Proposal>(`/proposals/${id}`);
    applyProposalUpdate(proposal);
  };

  // Clear all conversation state when the active project changes.
  // Deferred so it doesn't fire on initial mount.
  createEffect(
    on(
      projectId,
      () => {
        if (activeController) {
          activeController.abort();
          activeController = null;
        }
        setSelectedId(null);
        setLocalMessages([]);
        setConversationProposals({});
        setIsRespondingInternal(false);
        setStreamingContentInternal("");
        setRespondingConvId(null);
      },
      { defer: true },
    ),
  );

  const [conversationsResource, { refetch, mutate: mutateConversations }] =
    createResource(projectId, listConversations);

  // Replace the message list with what the server has, keeping any message the
  // server does not know about that the client still has a stake in: one
  // carrying a send failure, or one whose send() is still in flight. A fetch
  // racing a failing send would otherwise take the failed message — and the
  // inline indicator that is now its only report — with it (FLOW-692). A
  // fetch racing a send that's still in flight (e.g. the resource fetch a
  // just-created conversation's select() fires) would otherwise wipe the
  // optimistic message out from under it, leaving only the responding
  // indicator on screen (FLOW-812). A fetch the send overtook entirely —
  // handled before the send, resolving after it finished — is stale about the
  // whole exchange, so `overtakenBySend` widens the carry-over to every local
  // message the fetched list is missing rather than only those two cases
  // (FLOW-882).
  // Only for the conversation being synced: either message keeps a
  // client-minted id no server list will ever contain, so an unscoped
  // carry-over would follow the user into every conversation they open next.
  const syncMessages = (
    convId: ConversationId,
    fetched: Message[],
    overtakenBySend: boolean,
  ) =>
    setLocalMessages((prev) => {
      const fetchedIds = new Set(fetched.map((m) => m.id));
      const carried = prev.filter(
        (m) =>
          (overtakenBySend || m._clientError || pendingSendIds.has(m.id)) &&
          m.conversation_id === convId &&
          !fetchedIds.has(m.id),
      );
      return carried.length ? [...fetched, ...carried] : fetched;
    });

  // Fetch the conversation and sync it, flagging the list as superseded when a
  // send for the same conversation finished while the fetch was outstanding.
  const fetchAndSync = async (
    convId: ConversationId,
  ): Promise<ConversationWithMessages> => {
    const sendsBefore = sendCount(convId);
    const conv = await getConversation(convId);
    syncMessages(convId, conv.messages, sendCount(convId) !== sendsBefore);
    setConversationProposals(indexProposals(conv.proposals));
    return conv;
  };

  const [selectedResource, { mutate: mutateSelected }] = createResource(
    selectedId,
    async (id) => {
      if (!id) return undefined;
      // Syncs local messages and hydrated proposals with the fetched data.
      return fetchAndSync(id);
    },
  );

  // Only ever the selected conversation's messages. select() sets selectedId
  // synchronously but leaves localMessages alone until the new conversation's
  // fetch lands, so without this the previous conversation's thread — failed
  // messages and their Retry buttons included — renders under the new
  // conversation's header. Retry reads selectedId(), so a click in that window
  // would post the old conversation's text to the new one (FLOW-814).
  const messages = createMemo(() =>
    localMessages().filter((m) => m.conversation_id === selectedId()),
  );

  const select = (id: ConversationId | null) => {
    setSelectedId(id);
    if (id === null) {
      mutateSelected(undefined);
      setLocalMessages([]);
      setConversationProposals({});
      setIsRespondingInternal(false);
    }
  };

  const create = async (title?: string): Promise<Conversation> => {
    const conversation = await createConversation(projectId(), title);

    mutateConversations((prev) =>
      prev ? [conversation, ...prev] : [conversation],
    );

    select(conversation.id);

    return conversation;
  };

  const refetchMessages = async () => {
    const convId = selectedId();
    if (!convId) return;
    await fetchAndSync(convId);
  };

  const cancel = () => {
    if (activeController) {
      activeController.abort();
      activeController = null;
    }
  };

  // Send a message and stream AI response via SSE
  const send = async (
    content: string,
    viewContext?: ChatViewContext,
  ): Promise<ChatResponse> => {
    const convId = selectedId();
    if (!convId) {
      throw new Error("No conversation selected");
    }

    const controller = new AbortController();
    activeController = controller;

    // Optimistically add user message. Mirror the context_work_item_id the
    // backend will persist when viewContext is work_item, so the "while viewing"
    // indicator appears immediately instead of only after a refetch (FLOW-457).
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}` as ConversationId,
      conversation_id: convId,
      role: "user",
      content,
      context_work_item_id:
        viewContext?.type === "work_item" ? viewContext.workItemId : undefined,
      created_at: new Date().toISOString(),
    };
    setLocalMessages((prev) => [...prev, tempUserMessage]);
    pendingSendIds.add(tempUserMessage.id);

    setRespondingConvId(convId);
    setIsRespondingInternal(true);
    setStreamingContentInternal("");

    try {
      const response = await sendMessageStreaming(
        convId,
        content,
        viewContext,
        {
          onTextDelta: (text) => {
            setStreamingContentInternal((prev) => prev + text);
          },
        },
        controller.signal,
      );

      // Only update the visible message list if the user is still viewing the
      // originating conversation. Otherwise the response would land in whatever
      // conversation they switched to (FLOW-301). The backend has already
      // persisted the response, so it will reappear when they return.
      if (selectedId() === convId) {
        setLocalMessages((prev) => {
          const withoutTemp = prev.filter((m) => m.id !== tempUserMessage.id);
          const assistantMsg = response.correlation_id
            ? { ...response.message, correlation_id: response.correlation_id }
            : response.message;
          return [...withoutTemp, tempUserMessage, assistantMsg];
        });
      }

      if (response.generated_title) {
        const title = response.generated_title;
        mutateConversations((prev) =>
          prev?.map((c) => (c.id === convId ? { ...c, title } : c)),
        );
      }

      return response;
    } catch (err) {
      // On manual cancel: keep user message and preserve any partial response
      if (controller.signal.aborted) {
        const partial = streamingContentInternal();
        if (partial && selectedId() === convId) {
          const partialMessage: Message = {
            id: `partial-${Date.now()}` as ConversationId,
            conversation_id: convId,
            role: "assistant",
            content: partial,
            created_at: new Date().toISOString(),
          };
          setLocalMessages((prev) => [...prev, partialMessage]);
        }
        // Return a synthetic response so callers don't need to handle the error
        return {
          message: {
            id: `cancelled-${Date.now()}` as ConversationId,
            conversation_id: convId,
            role: "assistant",
            content: partial || "",
            created_at: new Date().toISOString(),
          },
          has_proposal: false,
        };
      }
      // On real errors: mark the message with error state instead of removing
      // it — but only in the conversation it belongs to. The user may have
      // switched away mid-send, and a failure reported in the chat now on
      // screen invites a Retry that would post this text there (FLOW-814).
      surfaceQuotaExhausted(err);
      const error = err instanceof Error ? err : new Error(String(err));
      if (selectedId() === convId) {
        setLocalMessages((prev) => {
          // syncMessages runs inside the resource fetcher, so a superseded
          // fetch for another conversation still executes it and drops this
          // message on the conversation-id scope. Put it back rather than
          // dropping the failure on the floor: the inline indicator is the
          // only report there is (FLOW-692).
          if (!prev.some((m) => m.id === tempUserMessage.id)) {
            return [
              ...prev,
              { ...tempUserMessage, _clientError: { error, attemptCount: 1 } },
            ];
          }
          return prev.map((m) =>
            m.id === tempUserMessage.id
              ? {
                  ...m,
                  _clientError: {
                    error,
                    attemptCount: (m._clientError?.attemptCount ?? 0) + 1,
                  },
                }
              : m,
          );
        });
      }

      throw err;
    } finally {
      setIsRespondingInternal(false);
      setStreamingContentInternal("");
      setRespondingConvId(null);
      activeController = null;
      // Only this send's id — concurrent sends (e.g. a retry started while
      // another send is still settling) must not clear each other's entries.
      pendingSendIds.delete(tempUserMessage.id);
      completedSends.set(convId, sendCount(convId) + 1);
    }
  };

  const updateTitle = async (
    id: ConversationId,
    title: string,
  ): Promise<void> => {
    await updateConversationTitle(id, title);
    mutateConversations((prev) =>
      prev?.map((c) => (c.id === id ? { ...c, title } : c)),
    );
  };

  const droppedEditNotice = (convId: ConversationId): string => {
    const title = conversationsResource()?.find((c) => c.id === convId)?.title;
    return `Your message wasn't sent because you switched conversations. It's waiting in ${
      title ? `"${title}"` : "that conversation"
    } with a Retry.`;
  };

  // By the time the resend guard below runs the DELETE has already cascaded
  // server-side: the edited message and everything after it are gone from
  // conversation A. Dropping the resend silently would discard text the user
  // authored and we deleted at our own request (FLOW-818). Put it back into
  // the conversation it was written for as a failed send, so returning there
  // shows it with the usual Retry — which, reading the selection like every
  // other send, can only post it to that conversation.
  const preserveDroppedEdit = (
    convId: ConversationId,
    content: string,
    viewContext?: ChatViewContext,
  ) => {
    const preserved: Message = {
      id: `dropped-${Date.now()}` as MessageId,
      conversation_id: convId,
      role: "user",
      content,
      context_work_item_id:
        viewContext?.type === "work_item" ? viewContext.workItemId : undefined,
      created_at: new Date().toISOString(),
      _clientError: {
        error: new Error(
          "Not sent: you switched conversations while this message was saving.",
        ),
        // Never attempted — the send was dropped before it went out, so a
        // failing Retry is attempt one.
        attemptCount: 0,
      },
    };
    setLocalMessages((prev) => [...prev, preserved]);
    options.onDroppedEdit?.(droppedEditNotice(convId));
  };

  // Delete a message (and all subsequent) from local state and API, then optionally re-send
  const deleteMessageAndResend = async (
    messageId: MessageId,
    resendContent?: string,
    viewContext?: ChatViewContext,
  ): Promise<void> => {
    const convId = selectedId();
    // Optimistically remove target + all messages at or after its created_at
    // (mirrors backend cascade). The backend's cascade stops at the
    // conversation boundary, so this one must too: localMessages spans every
    // conversation the session has touched, and a preserved dropped resend
    // carries a just-now created_at, so an unscoped filter let a delete in
    // one conversation sweep another's rescued text away (FLOW-818).
    setLocalMessages((prev) => {
      const target = prev.find((m) => m.id === messageId);
      if (!target) return prev;
      return prev.filter(
        (m) =>
          m.conversation_id !== target.conversation_id ||
          m.created_at < target.created_at,
      );
    });
    await apiDeleteMessage(messageId);
    if (resendContent === undefined) return;
    // The delete is a round trip, and send() reads the selection when it is
    // finally called — so a switch while it was in flight would post this
    // edit to the conversation now on screen (FLOW-814). The delete has
    // already happened; drop the resend rather than send it to the wrong
    // conversation, and preserve the text where it belongs (FLOW-818).
    if (selectedId() === convId) {
      await send(resendContent, viewContext);
    } else if (convId) {
      preserveDroppedEdit(convId, resendContent, viewContext);
    }
  };

  const retryMessage = async (
    messageId: MessageId,
    viewContext?: ChatViewContext,
  ): Promise<ChatResponse> => {
    // Safe to read the selection: messages() only ever exposes the selected
    // conversation's messages, so the lookup below cannot find one belonging
    // to another conversation (FLOW-814).
    const convId = selectedId();
    if (!convId) {
      throw new Error("No conversation selected");
    }

    const failedMsg = messages().find((m) => m.id === messageId);
    if (!failedMsg || failedMsg.role !== "user") {
      throw new Error("Message not found or not a user message");
    }

    const currentAttemptCount = failedMsg._clientError?.attemptCount ?? 0;

    // Remove the failed message; send() creates a fresh optimistic message
    // for the retry, so leaving the old one behind would duplicate it.
    setLocalMessages((prev) => prev.filter((m) => m.id !== messageId));
    // Snapshot what's left so a renewed failure can undo exactly what this
    // send() call adds, rather than guessing by content.
    const baselineIds = new Set(messages().map((m) => m.id));

    try {
      return await send(failedMsg.content, viewContext);
    } catch (err) {
      // send() already appended its own temp message with _clientError
      // (attemptCount reset to 1) before rethrowing. Drop that copy and
      // restore the original message id/content so attemptCount accumulates
      // across retries instead of duplicating the message.
      setLocalMessages((prev) => [
        ...prev.filter((m) => baselineIds.has(m.id)),
        {
          ...failedMsg,
          _clientError: {
            error: err instanceof Error ? err : new Error(String(err)),
            attemptCount: currentAttemptCount + 1,
          },
        },
      ]);
      throw err;
    }
  };

  const sortConversations = (convs: Conversation[]): Conversation[] =>
    [...convs].sort((a, b) => {
      if (a.is_starred !== b.is_starred) return a.is_starred ? -1 : 1;
      return b.updated_at.localeCompare(a.updated_at);
    });

  const toggleStar = async (conversationId: ConversationId): Promise<void> => {
    const target = conversationsResource()?.find(
      (c) => c.id === conversationId,
    );
    if (!target) return;
    const prior = target.is_starred;
    const newStarred = !prior;
    mutateConversations((list) =>
      list
        ? sortConversations(
            list.map((c) =>
              c.id === conversationId ? { ...c, is_starred: newStarred } : c,
            ),
          )
        : list,
    );
    try {
      const updated = await setConversationStarred(conversationId, newStarred);
      mutateConversations((list) =>
        list
          ? sortConversations(
              list.map((c) => (c.id === conversationId ? updated : c)),
            )
          : list,
      );
    } catch (err) {
      mutateConversations((list) =>
        list
          ? sortConversations(
              list.map((c) =>
                c.id === conversationId ? { ...c, is_starred: prior } : c,
              ),
            )
          : list,
      );
      throw err;
    }
  };

  const handleWebSocketEvent = (event: BoardEvent) => {
    switch (event.type) {
      case "MessageAdded":
        if (
          "conversation_id" in event.payload &&
          event.payload.conversation_id === selectedId()
        ) {
          const convId = selectedId();
          if (convId) {
            void fetchAndSync(convId);
          }
        }
        break;
    }
  };

  return {
    conversations: () => conversationsResource(),
    loading: () => conversationsResource.loading,
    error: () => conversationsResource.error,
    selectedConversation: () => selectedResource(),
    selectedLoading: () => selectedResource.loading,
    selectedError: () => selectedResource.error,
    messages,
    isResponding,
    streamingContent,
    select,
    create,
    send,
    cancel,
    refetchMessages,
    refetch,
    updateTitle,
    toggleStar,
    getProposal,
    applyProposalUpdate,
    refreshProposal,
    handleWebSocketEvent,
    deleteMessageAndResend,
    retryMessage,
  };
}
